import type { AuthorizationCase } from '@/services/authorization-case.service';
import type { Finding } from '@/services/inspection.service';
import type { StepExecutionView } from '@/features/inspection/services/procedure-execution.service';
import { CaseContextField, CaseContextFieldGrid } from './CaseContextField';

interface Props {
  kase: AuthorizationCase;
  step: StepExecutionView | null;
  finding: Finding | null;
}

export function WhyApprovalRequiredPanel({ kase, step, finding }: Props) {
  const procedureOrStep = kase.procedureStepExecutionId
    ? (step?.title ?? 'Loading…')
    : finding
      ? `Finding #${finding.findingNumber} — ${finding.title}`
      : '—';

  const businessRule = kase.approvalRequestTypeDescription?.trim()
    ? kase.approvalRequestTypeDescription
    : 'Not documented for this approval type — no description is configured on the approval request type catalog entry.';

  const estimatedWorkTime = finding?.estimatedRepairHours
    ? `${finding.estimatedRepairHours} hour(s) (estimated repair time from the linked finding)`
    : 'Not tracked for this request type — no estimated-time field exists at the procedure-step level.';

  return (
    <div className="rounded-xl border border-amber-300/60 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/10">
      <div className="border-b border-amber-300/60 px-5 py-3.5 dark:border-amber-900/40">
        <h3 className="text-sm font-semibold text-foreground">Why Approval Is Required</h3>
      </div>
      <div className="px-5 py-4">
        <CaseContextFieldGrid>
          <CaseContextField label="Procedure / Step" value={procedureOrStep} />
          <CaseContextField label="Business Rule" value={businessRule} />
          <CaseContextField label="Engineer Justification" value={kase.reason} />
          <CaseContextField
            label="Customer Impact"
            value="Not tracked in this system — no dedicated field captures customer impact today."
          />
          <CaseContextField label="Estimated Work Time" value={estimatedWorkTime} />
        </CaseContextFieldGrid>
      </div>
    </div>
  );
}
